import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Override showDialog to use show() instead of showModal() so header remains clickable
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // If body is already fixed (another drawer was open), preserve scroll position from body.style.top
    // Otherwise, use current window scroll position
    let scrollY;
    if (document.body.style.position === 'fixed' && document.body.style.top) {
      scrollY = -parseInt(document.body.style.top) || 0;
    } else {
      scrollY = window.scrollY;
    }
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Use show() instead of showModal() so header can remain clickable above the dialog
      dialog.show();
      this.dispatchEvent(new CustomEvent('dialog:open'));

      // Attach click handler directly to backdrop
      const backdrop = this.querySelector('.cart-drawer__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', this.#handleBackdropClick);
      }

      // Handle escape key at document level
      document.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Handle clicks to close dialog when clicking on backdrop
   */
  #handleBackdropClick = (event) => {
    // Close when clicking on backdrop
    this.closeDialog();
  };

  /**
   * Handle escape key to close dialog
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.dialog.open) return;
    
    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  };

  /**
   * Override closeDialog to restore scroll position
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Check if another drawer is opening (to preserve scroll position during transitions)
    // Check if drawer coordinator is transitioning OR if another drawer is actually open
    const coordinator = window.drawerCoordinator;
    const isAnotherDrawerOpening = document.body.hasAttribute('data-drawer-transitioning') || 
      (coordinator && coordinator.hasAnyDrawerOpen());

    // Remove event listeners
    const backdrop = this.querySelector('.cart-drawer__backdrop');
    if (backdrop) {
      backdrop.removeEventListener('click', this.#handleBackdropClick);
    }
    document.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Only restore scroll position if no other drawer is opening
    if (!isAnotherDrawerOpening) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    } else {
      // Another drawer is opening - don't remove fixed positioning
      // Keep body fixed so the scroll position is preserved in body.style.top
      // The new drawer will read from body.style.top when it opens
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new CustomEvent('dialog:close'));
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  toggle() {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
